/**
 * B31 — §7 step 4: the payment gateway seam over Stripe elements. Card
 * fields render inside OUR origin (never the tenant page); the script
 * itself MUST come from js.stripe.com (PCI) — the hand-rolled loader
 * below injects exactly the script @stripe/stripe-js would (that package
 * is the same script-injection wrapper), keeping the repo's zero-new-
 * runtime-dep stance.
 *
 * B177: the element renders in Stripe's iframe, so its chrome is themed
 * through the Elements `appearance` object or not at all — the gateway
 * carries the derived appearance in at construction and can re-apply it
 * when the buyer toggles the theme mid-payment.
 *
 * Fail closed: no factory (script blocked), no publishableKey (channel
 * config absent on the stage), or no clientSecret → NO gateway, and the
 * Pay button stays walled with the reason named. A refused confirm keeps
 * the order PENDING_PAYMENT — §7's retry-until-hold-expiry arrow.
 */

import type { PaymentAppearance } from "./paymentAppearance.js";
import type { BuyerRefusalField } from "./buyerAttach.js";

/* Minimal structural types for the slice of Stripe.js we drive. */
export interface PaymentElementLike {
  mount(selector: string): void;
  unmount?(): void;
  /** B511 — the Express Checkout Element's event seam (the card element never needs it). */
  on?(event: "ready" | "click" | "confirm" | "cancel", handler: (event: ExpressEventLike) => void): void;
}
export interface ElementsLike {
  create(kind: "payment" | "expressCheckout", options?: ExpressCheckoutOptions | PaymentElementOptions): PaymentElementLike;
  /** B177: Stripe's live re-theme; B397 leg (3): the deferred re-quote. Optional — older builds lack it. */
  update?(options: { appearance?: PaymentAppearance; amount?: number }): void;
  /** B397 leg (3) — the deferred flow validates the collected fields before any intent exists. */
  submit?(): Promise<{ readonly error?: { readonly message?: string } }>;
}
/**
 * B519 — measured 2026-09-22 against the dev test key: Link rides INSIDE the
 * Payment Element whatever the group's types say (`paymentMethodTypes` and
 * `allowedPaymentMethodTypes` both still drew Card / Bank ($5 back) / Klarna
 * tabs and a save-with-Link panel). Switching Link off on the ELEMENT is what
 * draws the card fields alone — 73:2's PAY BY CARD. Wallets live in the
 * Express Checkout row above (B511).
 */
export interface PaymentElementOptions {
  readonly wallets: { readonly link: "never" };
}
export const CARD_ONLY_ELEMENT: PaymentElementOptions = { wallets: { link: "never" } };

export interface StripeConfirmResult {
  readonly error?: { readonly message?: string };
  readonly paymentIntent?: { readonly status: string };
}
/**
 * B397 leg (3) — Stripe's deferred-intent construction (73:2: the merged
 * cart & pay page draws card fields BEFORE an order exists, and the LATE
 * rule forbids minting one to decorate a page). `mode: "payment"` quotes
 * the element from the DISPLAY total; the real intent is created at PAY
 * and its clientSecret rides `confirmPayment` instead of `elements()`.
 */
export interface DeferredElementsOptions {
  readonly mode: "payment";
  readonly amount: number;
  /** Stripe speaks lowercase ISO ("usd"); the gateway translates. */
  readonly currency: string;
  /**
   * B403 finding #27 (Pathik's live 4242 buy, 2026-08-26): a deferred
   * element left on automatic payment methods renders the dashboard's
   * whole method row (Link/Bank/Cash App/Amazon/Klarna) and makes
   * `confirmPayment` demand `confirmParams.return_url` — the buy crashed
   * at PAY. 73:2 draws PAY BY CARD with card fields ONLY, so the element
   * is pinned to card: the frame's face and the working confirm are the
   * same constraint. (The server-side intent already says
   * allow_redirects:"never" — this is the client half of that fact.)
   */
  /* B519 — camelCase is the key Stripe.js READS: the snake_case spelling
     (the REST API's) shipped here and was silently ignored, leaving the
     element on the dashboard's automatic methods. */
  readonly paymentMethodTypes?: readonly string[];
  readonly appearance?: PaymentAppearance;
}
export interface StripeLike {
  elements(options: { clientSecret: string; appearance?: PaymentAppearance } | DeferredElementsOptions): ElementsLike;
  confirmPayment(options: {
    elements: ElementsLike;
    redirect: "if_required";
    /** B397 leg (3): the deferred flow's secret, minted at PAY. */
    clientSecret?: string;
  }): Promise<StripeConfirmResult>;
}
/** B91 GAP-6 — the optional second argument mirrors Stripe.js: Stripe(pk, { stripeAccount }). */
export type StripeFactory = (publishableKey: string, options?: { readonly stripeAccount?: string }) => StripeLike | undefined;

export type ConfirmOutcome =
  | { readonly ok: true }
  /** B603 — a refusal about a field the buyer can SEE names it, so the caller
   * can seat the sentence at that field instead of the page notice (R18). */
  | { readonly ok: false; readonly message: string; readonly atField?: BuyerRefusalField };

export interface PaymentGateway {
  mount(selector: string): void;
  unmount(): void;
  /** B177: re-theme a mounted element when the buyer flips the theme. */
  setAppearance(appearance: PaymentAppearance): void;
  confirm(): Promise<ConfirmOutcome>;
}

/**
 * Builds the gateway or explains why it cannot exist yet. Pure over the
 * injected factory — tests drive it without a browser or Stripe account.
 */
export function createPaymentGateway(options: {
  readonly factory: StripeFactory | undefined;
  readonly publishableKey: string | undefined;
  /** B91 GAP-6 — the tenant's onboarded connected account: Stripe.js mounts on it. */
  readonly stripeAccount?: string;
  readonly clientSecret: string;
  /** B177: the app theme in Stripe's language (see paymentAppearance.ts). */
  readonly appearance?: PaymentAppearance;
}): { gateway: PaymentGateway } | { gateway?: undefined; unavailable: string } {
  if (options.factory === undefined) return { unavailable: "the payment script did not load" };
  if (options.publishableKey === undefined || options.publishableKey === "")
    return { unavailable: "card payment is not configured for this channel yet" };
  const stripe =
    options.stripeAccount === undefined
      ? options.factory(options.publishableKey)
      : options.factory(options.publishableKey, { stripeAccount: options.stripeAccount });
  if (stripe === undefined) return { unavailable: "the payment script did not load" };

  const elements = stripe.elements({
    clientSecret: options.clientSecret,
    ...(options.appearance === undefined ? {} : { appearance: options.appearance }),
  });
  let element: PaymentElementLike | undefined;

  return {
    gateway: {
      mount: (selector) => {
        element ??= elements.create("payment", CARD_ONLY_ELEMENT);
        element.mount(selector);
      },
      unmount: () => {
        /* B397 walk reopen (2026-08-21): unmount removes the iframe but the
           element STAYS in the elements group — Stripe refuses a second
           create('payment') on one group with the IntegrationError that
           blanked the deployed page. Keep the instance; mount() reuses it
           (the buyer's typed card details survive the remount too). */
        element?.unmount?.();
      },
      setAppearance: (appearance) => {
        elements.update?.({ appearance });
      },
      confirm: async () => {
        const result = await stripe.confirmPayment({ elements, redirect: "if_required" });
        if (result.error !== undefined) return { ok: false, message: result.error.message ?? "Payment was declined." };
        const status = result.paymentIntent?.status;
        if (status === "succeeded" || status === "processing") return { ok: true };
        return { ok: false, message: `payment did not complete (status: ${status ?? "unknown"})` };
      },
    },
  };
}

/* ------------------------------------------------------------ B397 leg (3) */

export interface DeferredPaymentGateway {
  mount(selector: string): void;
  unmount(): void;
  /** B177: re-theme a mounted element when the buyer flips the theme. */
  setAppearance(appearance: PaymentAppearance): void;
  /** The total moved (a promo fired, a line changed): re-quote the element. */
  setAmount(amount: number): void;
  /**
   * The LATE moment (B395's rule, worn by the buyer's page): the fields are
   * VALIDATED first (`elements.submit()` — a refusal names the field and no
   * order is minted for a typo), THEN `mint` runs (cart.checkout +
   * intent.create, the caller's arrows), then the fresh secret confirms.
   * A refused mint is a refusal with the caller's own sentence.
   */
  confirmWith(mint: () => Promise<PayMint>): Promise<ConfirmOutcome>;
  /**
   * B511 — mount the wallet row (Express Checkout Element) on the SAME
   * deferred group. False when this Stripe.js cannot serve it (no event
   * seam) — the card path stands alone then.
   */
  mountExpress(selector: string, handlers: ExpressHandlers): boolean;
  unmountExpress(): void;
}

/* ------------------------------------------------------------------ B511 */

/**
 * B511 — the slice of Stripe's Express Checkout Element events we drive
 * (one structural shape; each event carries its own members). `ready`
 * names the wallets the device can serve; `click` must resolve or reject
 * within a second; `confirm` carries the wallet sheet's billing details.
 */
export interface ExpressEventLike {
  readonly availablePaymentMethods?: Readonly<Record<string, boolean>> | undefined;
  readonly billingDetails?: WalletBillingDetails | undefined;
  readonly expressPaymentType?: string;
  resolve?(payload?: Record<string, never>): void;
  reject?(): void;
  paymentFailed?(payload?: { readonly reason?: "fail" }): void;
}
export interface WalletBillingDetails {
  readonly name?: string | null;
  readonly email?: string | null;
  readonly phone?: string | null;
}
/**
 * Create-time options. Stripe moved `emailRequired`/`billingAddressRequired`
 * from the click payload to create (the click-time spelling is deprecated)
 * — the effect the ruling asks for is the same: the sheet collects them.
 */
export interface ExpressCheckoutOptions {
  readonly emailRequired: true;
  readonly billingAddressRequired: true;
  /** The wallets' OWN branded buttons (Pathik: "ofcourse use the native button") — only the theme and height are ours. */
  readonly buttonTheme: { readonly applePay: "black" | "white"; readonly googlePay: "black" | "white" };
  readonly buttonHeight: number;
}
/** 356-2: the wallet row's pills sit at the card's control height. */
export const EXPRESS_BUTTON_HEIGHT = 44;

/** The buttons wear the surface: white on the night theme, black on the light one. */
export function expressButtonTheme(appearance: PaymentAppearance | undefined): ExpressCheckoutOptions["buttonTheme"] {
  const tone = appearance?.theme === "night" ? "white" : "black";
  return { applePay: tone, googlePay: tone };
}

/** The wallets the device can actually serve — an empty list keeps the row (and its divider) away. */
export function walletMethods(available: Readonly<Record<string, boolean>> | undefined): readonly string[] {
  if (available === undefined) return [];
  return Object.entries(available)
    .filter(([, on]) => on)
    .map(([name]) => name);
}

export type BuyerField = "name" | "email";
export interface WalletBuyerTyped {
  readonly name: string;
  readonly email: string;
}
/**
 * The buyer a wallet press books under: the wallet's details first, what
 * the buyer typed fills any gap. Anything still missing is NAMED, with the
 * half the wallet did give kept for the fields — the ruling's second
 * sentence: never place an order without a destination for the tickets.
 */
export function walletBuyer(
  details: WalletBillingDetails | undefined,
  typed: WalletBuyerTyped,
):
  | { readonly ok: true; readonly buyer: WalletBuyerTyped }
  | { readonly ok: false; readonly missing: readonly BuyerField[]; readonly partial: Partial<WalletBuyerTyped> } {
  const pick = (wallet: string | null | undefined, own: string): string => {
    const w = (wallet ?? "").trim();
    return w !== "" ? w : own.trim();
  };
  const name = pick(details?.name, typed.name);
  const email = pick(details?.email, typed.email);
  const missing: BuyerField[] = [];
  if (name === "") missing.push("name");
  if (email === "") missing.push("email");
  if (missing.length === 0) return { ok: true, buyer: { name, email } };
  return { ok: false, missing, partial: { ...(name !== "" ? { name } : {}), ...(email !== "" ? { email } : {}) } };
}

export type ExpressOutcome =
  | { readonly ok: true; readonly buyer: WalletBuyerTyped }
  | { readonly ok: false; readonly message: string; readonly atField?: BuyerRefusalField }
  | {
      readonly ok: false;
      readonly message: string;
      readonly missing: readonly BuyerField[];
      readonly partial: Partial<WalletBuyerTyped>;
    };

/** The caller's side of a wallet press. */
export interface ExpressHandlers {
  /** What the buyer has typed so far — fills what the wallet leaves out. */
  typed(): WalletBuyerTyped;
  /** B296's ONE sentence for a shut PAY (no tickets, no departure) — the sheet does not open over it. */
  walled(): string | undefined;
  onReady(methods: readonly string[]): void;
  /** The sheet authorized — the order is being placed (the busy face). B524: which wallet, when Stripe says. */
  onStart(walletType?: string): void;
  /** cart.checkout + intent.create for this buyer — the same mint the card PAY runs. */
  mint(buyer: WalletBuyerTyped): Promise<PayMint>;
  onOutcome(outcome: ExpressOutcome): void;
}

export function missingWords(missing: readonly BuyerField[]): string {
  const words = missing.map((f) => (f === "name" ? "the name for the booking" : "the email for your tickets"));
  return `Your wallet didn’t share ${words.join(" or ")} — add it below, then pay again.`;
}

/** What PAY's mint step yields: the intent's secret, or the refusal to state.
 * B478: the mint may also carry the KERNEL's checkout total in minor units —
 * when it differs from the page's quote the gateway re-quotes and re-submits
 * before confirming, so the intent and the element never disagree. */
export type PayMint =
  | { readonly clientSecret: string; readonly amount?: number; readonly refused?: never; readonly atField?: never }
  /** B603 — a mint refused over a buyer-typed field carries WHICH field, so
   * both pay paths can seat the refusal at it rather than in a banner. */
  | { readonly refused: string; readonly atField?: BuyerRefusalField; readonly clientSecret?: never; readonly amount?: never };

/**
 * The deferred-intent gateway (73:2). Same fail-closed walls as
 * `createPaymentGateway`, plus one of its own: a page whose display total
 * is not a positive figure mounts NO card fields — an unpriced cart's
 * honest face is the absence, never a £0 quote (§10).
 */
export function createDeferredPaymentGateway(options: {
  readonly factory: StripeFactory | undefined;
  readonly publishableKey: string | undefined;
  /** B91 GAP-6 — the tenant's onboarded connected account: the deferred element can only confirm secrets minted on it. */
  readonly stripeAccount?: string;
  /** The cart's display total in minor units — the element's quote, not the charge. */
  readonly amount: number;
  readonly currency: string;
  readonly appearance?: PaymentAppearance;
}): { gateway: DeferredPaymentGateway } | { gateway?: undefined; unavailable: string } {
  if (options.factory === undefined) return { unavailable: "the payment script did not load" };
  if (options.publishableKey === undefined || options.publishableKey === "")
    return { unavailable: "card payment is not configured for this channel yet" };
  if (!Number.isFinite(options.amount) || options.amount <= 0)
    return { unavailable: "the cart's total is not known yet" };
  const stripe =
    options.stripeAccount === undefined
      ? options.factory(options.publishableKey)
      : options.factory(options.publishableKey, { stripeAccount: options.stripeAccount });
  if (stripe === undefined) return { unavailable: "the payment script did not load" };

  const elements = stripe.elements({
    mode: "payment",
    amount: options.amount,
    currency: options.currency.toLowerCase(),
    /* B403 #27 — card only: the frame's PAY BY CARD face, and the reason
       confirmPayment needs no return_url (no redirect method can render). */
    paymentMethodTypes: ["card"],
    ...(options.appearance === undefined ? {} : { appearance: options.appearance }),
  });
  let element: PaymentElementLike | undefined;
  let mounted = false;
  let quotedAmount = options.amount;

  /*
   * B478 — Pathik's live "Your card number is incomplete" on a complete,
   * brand-recognized card. pay() dispatches CHECKOUT_OK and setTotal()
   * inside the MINT, React re-renders mid-confirm, and the App's effects
   * unmount, remount and re-quote this group BETWEEN elements.submit()
   * and confirmPayment — Stripe then validates a freshly re-created
   * element whose typed state has not been restored and refuses the buy.
   * The gateway is the one place every caller crosses, so the wall lives
   * here: while a confirm is in flight the group HOLDS STILL — external
   * mount/unmount/setAmount/setAppearance are recorded and applied after
   * the confirm settles. A held act is never lost, only late.
   */
  let inFlight = false;
  let heldMountState: boolean | undefined;
  let heldSelector: string | undefined;
  let heldAmount: number | undefined;
  let heldAppearance: PaymentAppearance | undefined;

  const doMount = (selector: string): void => {
    element ??= elements.create("payment", CARD_ONLY_ELEMENT);
    element.mount(selector);
    mounted = true;
  };
  const doUnmount = (): void => {
    /* B397 walk reopen (2026-08-21): unmount removes the iframe but the
       element STAYS in the elements group — Stripe refuses a second
       create('payment') on one group with the IntegrationError that
       blanked the deployed page. Keep the instance; mount() reuses it
       (the buyer's typed card details survive the remount too). */
    element?.unmount?.();
    mounted = false;
  };
  const settle = (): void => {
    inFlight = false;
    if (heldMountState === false && mounted) doUnmount();
    else if (heldMountState === true && !mounted && heldSelector !== undefined) doMount(heldSelector);
    if (heldAmount !== undefined && heldAmount !== quotedAmount) {
      quotedAmount = heldAmount;
      elements.update?.({ amount: heldAmount });
    }
    if (heldAppearance !== undefined) elements.update?.({ appearance: heldAppearance });
    heldMountState = undefined;
    heldSelector = undefined;
    heldAmount = undefined;
    heldAppearance = undefined;
  };

  /** The shared tail of both presses: the kernel's total wins (B478), then confirm against the minted secret. */
  const confirmMinted = async (clientSecret: string, amount: number | undefined): Promise<ConfirmOutcome> => {
    if (amount !== undefined && amount !== quotedAmount) {
      /* B478 — the kernel's checkout total outranks the page quote:
         re-quote the group and RE-SUBMIT (Stripe's deferred rule —
         an update invalidates the earlier submit), then confirm. */
      if (elements.update === undefined || elements.submit === undefined)
        return { ok: false, message: "the payment script is too old to re-quote the total on this page" };
      elements.update({ amount });
      quotedAmount = amount;
      const resubmitted = await elements.submit();
      if (resubmitted.error !== undefined)
        return { ok: false, message: resubmitted.error.message ?? "Check your card details." };
    }
    const result = await stripe.confirmPayment({ elements, redirect: "if_required", clientSecret });
    if (result.error !== undefined) return { ok: false, message: result.error.message ?? "Payment was declined." };
    const status = result.paymentIntent?.status;
    if (status === "succeeded" || status === "processing") return { ok: true };
    return { ok: false, message: `payment did not complete (status: ${status ?? "unknown"})` };
  };

  /*
   * B511 — the wallet row. ONE Express Checkout Element per group, kept
   * across remounts exactly like the card element (B397's reopen), and
   * held still while a confirm is in flight (B478's argument: a wallet
   * press re-renders the page mid-confirm just as PAY does).
   */
  let express: PaymentElementLike | undefined;
  let expressHandlers: ExpressHandlers | undefined;
  let expressMounted = false;
  /* An element without the event seam is refused ONCE — Stripe refuses a second create on one group. */
  let expressRefused = false;
  let heldExpress: { readonly selector: string } | "unmount" | undefined;

  const onWalletConfirm = async (event: ExpressEventLike): Promise<void> => {
    const h = expressHandlers;
    if (h === undefined) return;
    const who = walletBuyer(event.billingDetails, h.typed());
    if (!who.ok) {
      /* The ruling's second sentence: no destination for the tickets → no
         order. The sheet closes; the fields ask, carrying what the wallet gave. */
      event.paymentFailed?.({ reason: "fail" });
      h.onOutcome({ ok: false, message: missingWords(who.missing), missing: who.missing, partial: who.partial });
      return;
    }
    if (elements.submit === undefined) {
      event.paymentFailed?.({ reason: "fail" });
      h.onOutcome({ ok: false, message: "the payment script is too old to take a wallet payment on this page" });
      return;
    }
    h.onStart(event.expressPaymentType);
    inFlight = true;
    let outcome: ExpressOutcome;
    try {
      const submitted = await elements.submit();
      if (submitted.error !== undefined) {
        event.paymentFailed?.({ reason: "fail" });
        outcome = { ok: false, message: submitted.error.message ?? "The wallet payment could not be started." };
      } else {
        const minted = await h.mint(who.buyer);
        if (minted.refused !== undefined) {
          event.paymentFailed?.({ reason: "fail" });
          outcome = { ok: false, message: minted.refused, ...(minted.atField !== undefined ? { atField: minted.atField } : {}) };
        } else {
          const confirmed = await confirmMinted(minted.clientSecret, minted.amount);
          outcome = confirmed.ok ? { ok: true, buyer: who.buyer } : confirmed;
        }
      }
    } catch (error) {
      event.paymentFailed?.({ reason: "fail" });
      outcome = { ok: false, message: error instanceof Error && error.message !== "" ? error.message : "Payment was declined." };
    } finally {
      settle();
      settleExpress();
    }
    h.onOutcome(outcome);
  };

  const doMountExpress = (selector: string): void => {
    express?.mount(selector);
    expressMounted = true;
  };
  const doUnmountExpress = (): void => {
    express?.unmount?.();
    expressMounted = false;
  };
  const settleExpress = (): void => {
    const held = heldExpress;
    heldExpress = undefined;
    if (held === "unmount" && expressMounted) doUnmountExpress();
    else if (held !== undefined && held !== "unmount" && !expressMounted) doMountExpress(held.selector);
  };

  return {
    gateway: {
      mount: (selector) => {
        if (inFlight) {
          heldMountState = true;
          heldSelector = selector;
          return;
        }
        doMount(selector);
      },
      unmount: () => {
        if (inFlight) {
          heldMountState = false;
          return;
        }
        doUnmount();
      },
      setAppearance: (appearance) => {
        if (inFlight) {
          heldAppearance = appearance;
          return;
        }
        elements.update?.({ appearance });
      },
      setAmount: (amount) => {
        if (inFlight) {
          heldAmount = amount;
          return;
        }
        quotedAmount = amount;
        elements.update?.({ amount });
      },
      confirmWith: async (mint) => {
        if (elements.submit === undefined)
          return { ok: false, message: "the payment script is too old to take card details on this page" };
        inFlight = true;
        try {
          const submitted = await elements.submit();
          if (submitted.error !== undefined)
            return { ok: false, message: submitted.error.message ?? "Check your card details." };
          const minted = await mint();
          if (minted.refused !== undefined)
            return { ok: false, message: minted.refused, ...(minted.atField !== undefined ? { atField: minted.atField } : {}) };
          return await confirmMinted(minted.clientSecret, minted.amount);
        } finally {
          settle();
        }
      },
      mountExpress: (selector, handlers) => {
        expressHandlers = handlers;
        if (expressRefused) return false;
        if (express === undefined) {
          const created = elements.create("expressCheckout", {
            emailRequired: true,
            billingAddressRequired: true,
            buttonTheme: expressButtonTheme(options.appearance),
            buttonHeight: EXPRESS_BUTTON_HEIGHT,
          });
          if (created.on === undefined) {
            expressRefused = true;
            return false;
          }
          created.on("ready", (event) => expressHandlers?.onReady(walletMethods(event.availablePaymentMethods)));
          created.on("click", (event) => {
            const wall = expressHandlers?.walled();
            if (wall !== undefined) {
              event.reject?.();
              expressHandlers?.onOutcome({ ok: false, message: wall });
              return;
            }
            event.resolve?.({});
          });
          created.on("confirm", (event) => void onWalletConfirm(event));
          express = created;
        }
        if (inFlight) {
          heldExpress = { selector };
          return true;
        }
        doMountExpress(selector);
        return true;
      },
      unmountExpress: () => {
        if (inFlight) {
          heldExpress = "unmount";
          return;
        }
        doUnmountExpress();
      },
    },
  };
}

const STRIPE_SRC = "https://js.stripe.com/v3";

declare global {
  interface Window {
    Stripe?: (publishableKey: string, options?: { readonly stripeAccount?: string }) => StripeLike;
  }
}

/** Browser-only: inject Stripe.js once and adapt window.Stripe to a factory. */
export function loadStripeFactory(doc: Document = document): Promise<StripeFactory | undefined> {
  if (window.Stripe !== undefined) {
    const stripe = window.Stripe;
    return Promise.resolve((key, opts) => (opts === undefined ? stripe(key) : stripe(key, opts)));
  }
  return new Promise((resolve) => {
    const script = doc.createElement("script");
    script.src = STRIPE_SRC;
    script.async = true;
    script.onload = () => {
      const stripe = window.Stripe;
      resolve(stripe === undefined ? undefined : (key, opts) => (opts === undefined ? stripe(key) : stripe(key, opts)));
    };
    script.onerror = () => resolve(undefined);
    doc.head.appendChild(script);
  });
}
